import { Controller } from "react-hook-form";
import Button from "components/button";
import Icon from "components/icon";
import Input, { Textarea } from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useAddDistanceTypeForm from "./useAddDistanceTypeForm";

export const addDistanceTypePageRouteName = "/config/distance-type/add";

const AddDistanceTypePage = () => {
  const {
    control,
    register,
    errors,
    fields,
    editingIndex,
    distanceCondition,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    handleConfirmCancel,
    getDistanceDisplayText,
    t,
  } = useAddDistanceTypeForm();

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Left Panel - Form */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("section-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {editingIndex !== null
                ? t("section-subtitle-edit")
                : t("section-subtitle-add")}
            </Typography>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddToList();
            }}
            className="flex flex-col gap-4"
          >
            {/* Distance Type Name */}
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-distance-type-name")}{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <Input
                placeholder={t("label-distance-type-name")}
                required
                {...register("distanceTypeName")}
                error={errors.distanceTypeName}
                data-testid="input-distance-type-name"
              />
            </div>

            {/* Distance (Km) */}
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-distance")}{" "}
                <span className="text-error-500">*</span>
              </Typography>

              <div className="flex items-start gap-2">
                {/* Condition Dropdown */}
                <div className="w-[140px] shrink-0">
                  <Controller
                    name="distanceCondition"
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={[
                          { label: t("condition-less-than"), value: "below" },
                          { label: t("condition-between"), value: "between" },
                          { label: t("condition-more-than"), value: "above" },
                        ]}
                        value={field.value}
                        onValueChange={(val) => field.onChange(val)}
                        data-testid="dropdown-distance-condition"
                      />
                    )}
                  />
                </div>

                {/* Value Inputs - conditional based on mode */}
                {distanceCondition === "between" ? (
                  <div className="flex items-start gap-2 flex-1">
                    <Input
                      enableNumberFormat
                      type="number"
                      placeholder="0"
                      {...register("distanceMin")}
                      error={errors.distanceMin}
                      data-testid="input-distance-min-value"
                    />
                    <div className="flex items-center gap-2 shrink-0 pt-2.5">
                      <span className="text-xs font-medium text-white bg-primary-500 rounded px-2 py-0.5">{t("label-distance-min")}</span>
                    </div>
                    <Input
                      enableNumberFormat
                      type="number"
                      placeholder="0"
                      {...register("distanceMax")}
                      error={errors.distanceMax}
                      data-testid="input-distance-max-value"
                    />
                    <div className="flex items-center gap-2 shrink-0 pt-2.5">
                      <span className="text-xs font-medium text-white bg-primary-500 rounded px-2 py-0.5">{t("label-distance-max")}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1">
                    <Input
                      enableNumberFormat
                      type="number"
                      placeholder="0"
                      {...register("distanceValue")}
                      error={errors.distanceValue}
                      data-testid={distanceCondition === "below" ? "input-distance-max-value" : "input-distance-min-value"}
                    />
                  </div>
                )}
              </div>

              {/* Hint text */}
              <Typography variant="body" tone="muted" className="text-xs">
                {distanceCondition === "below" && t("hint-less-than")}
                {distanceCondition === "between" && t("hint-between")}
                {distanceCondition === "above" && t("hint-more-than")}
              </Typography>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-description")}
              </Typography>
              <Textarea
                placeholder={t("label-description")}
                {...register("description")}
                error={errors.description}
                data-testid="textarea-distance-description"
              />
            </div>

            {/* Status */}
            <Controller
              name="status"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Typography variant="bodyLarge">
                    {t("label-status")}{" "}
                    <span className="text-error-500">*</span>
                  </Typography>
                  <Select
                    options={[
                      { label: t("status-active"), value: "active" },
                      { label: t("status-inactive"), value: "inactive" },
                    ]}
                    value={field.value}
                    onValueChange={(val) => field.onChange(val)}
                    error={fieldState.error}
                    data-testid="dropdown-distance-status"
                  />
                </div>
              )}
            />

            {/* Action Buttons */}
            {editingIndex !== null ? (
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  color="tertiary"
                  size="medium"
                  isFullSize
                  onClick={handleDiscardChanges}
                  data-testid="btn-discard-changes-distance-type"
                >
                  {t("btn-discard-changes")}
                </Button>
                <Button
                  type="submit"
                  variant="fill"
                  color="primary"
                  size="medium"
                  isFullSize
                  data-testid="btn-save-changes-distance-type"
                >
                  {t("btn-save-changes")}
                </Button>
              </div>
            ) : (
              <Button
                type="submit"
                variant="fill"
                color="primary"
                size="medium"
                isFullSize
                data-testid="btn-add-more-distance"
              >
                {t("btn-add-to-list")}
              </Button>
            )}
          </form>
        </section>

        {/* Right Panel - Items List */}
        <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1 mb-6">
            <Typography variant="heading4" as="h2">
              {t("list-title")}
            </Typography>
            <Typography variant="body" tone="muted">
              {t("list-subtitle")}
            </Typography>
          </div>

          {fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Icon
                name="document-text"
                size={48}
                className="text-greyScale-60 mb-4"
              />
              <Typography variant="body" tone="muted">
                {t("no-data-title")}
              </Typography>
              <Typography variant="body" tone="muted">
                {t("no-data-subtitle")}
              </Typography>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto">
              {fields.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between rounded-xl border p-4 ${
                    editingIndex === index
                      ? "border-primary-500 bg-primary-50"
                      : "border-border"
                  }`}
                >
                  <div className="flex flex-col gap-1">
                    <Typography variant="bodyLarge" className="font-semibold">
                      {item.distanceTypeName}
                    </Typography>
                    <div className="flex items-center gap-2">
                      <Typography variant="body">
                        {t("label-distance")}
                      </Typography>
                      <Typography variant="body">:</Typography>
                      <Typography variant="body" tone="muted">
                        {getDistanceDisplayText(item)}
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <Typography variant="body">
                        {t("label-description")}
                      </Typography>
                      <Typography variant="body">:</Typography>
                      <Typography variant="body" tone="muted">
                        {item.description || "-"}
                      </Typography>
                    </div>
                    <div className="flex items-center gap-2">
                      <Typography variant="body">
                        {t("label-status")}
                      </Typography>
                      <Typography variant="body">:</Typography>
                      <Typography variant="body" className="capitalize" tone="muted">
                        {item.status}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <button
                      type="button"
                      onClick={() => handleEditItem(index)}
                      className={`flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer border ${
                        editingIndex === index
                          ? "bg-primary-500 text-white border-primary-500"
                          : "hover:bg-greyScale-90 text-primary-500 border-primary-500"
                      }`}
                      data-testid={`btn-edit-row-distance-type-${index}`}
                    >
                      <Icon name="edit-2" size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteItem(index)}
                      className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-error-50 text-error-500 transition-colors cursor-pointer border border-error-500"
                      data-testid={`btn-delete-row-distance-${index}`}
                    >
                      <Icon name="trash" size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={handleCancel}
          data-testid="btn-cancel-distance"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={() => setShowSaveModal(true)}
          disabled={fields.length === 0}
          data-testid="btn-save-distance"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleSave}
        onCancel={() => setShowSaveModal(false)}
        testId="btn-save-confirmation-distance-type"
      />

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-cancel-title")}
        message={t("modal-cancel-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        testId="btn-cancel-confirmation-distance-type"
      />
    </div>
  );
};

export default AddDistanceTypePage;
